/**
 * Compact, self-contained Code 128 Barcode SVG generator.
 * Fully offline, zero external CDN dependencies (conforms to ADR-080).
 */

// Code 128 bar patterns for symbols 0 to 106.
// Each number string represents alternating bar and space widths (total width = 11 modules, stop is 13).
export const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112"
];

const START_CODE_B = 104;
const STOP_CODE = 106;

/**
 * Checks if a string can be encoded in Code 128 Subset B.
 */
export function isValidCode128B(text: string): boolean {
  if (!text) return false;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 32 || code > 126) return false;
  }
  return true;
}

/**
 * Encodes an ASCII string into Code 128 Subset B symbol indices.
 * Throws an Error if text is empty or contains non-ASCII characters.
 */
export function encodeCode128B(text: string): number[] {
  if (!text || text.length === 0) {
    throw new Error("متن بارکد نمی‌تواند خالی باشد.");
  }
  const symbols: number[] = [START_CODE_B];
  let checksum = START_CODE_B;

  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 32 || code > 126) {
      throw new Error(`کاراکتر غیرمجاز '${text[i]}' در بارکد Code 128 (کد اسکی ${code}). فقط کاراکترهای اسکی ۳۲ تا ۱۲۶ مجازند.`);
    }
    const val = code - 32;
    symbols.push(val);
    checksum += val * (i + 1);
  }

  const checkSymbol = checksum % 103;
  symbols.push(checkSymbol);
  symbols.push(STOP_CODE);

  return symbols;
}

/**
 * آستانه تجربی حداقل بعد X (عرض باریک‌ترین ماژول) برای برچسب‌های رهگیری داخلی کارخانه:
 * - در استانداردهای زنجیره توزیع عمومی (مانند GS1-128)، حداقل بعد X برابر 0.250 mm (کاربردهای ویژه) و 0.495 mm (توزیع سراسری) است.
 * - برای برچسب‌های تولید و انبار داخلی کارخانه، جهت جلوگیری از محوشدگی زیرپیکسلی (Sub-pixel aliasing) در رندر برداری و اسکن پایدار با اسکنرهای صنعتی دستی (در 96 DPI وب که هر میلی‌متر ~ 3.78 پیکسل است):
 *   مقدار 0.75 px (تقریباً 0.20 mm) به عنوان حداقل بعد X تعریف شده است.
 * - در صورتی که عرض اختصاص‌یافته به بارکد کمتر از این مقدار باشد، صفت data-density-warning="true" ثبت شده و در محیط طراح هشدار تراکم بیش از حد نمایش داده می‌شود.
 */
export const MIN_MODULE_WIDTH_PX = 0.75;
export const ISO_MIN_MODULE_WIDTH_PX = MIN_MODULE_WIDTH_PX;

export interface BarcodeSvgOptions {
  width?: number;
  height?: number;
  showText?: boolean;
  barColor?: string;
  bgColor?: string;
  minModuleWidth?: number;
  autoExpandWidth?: boolean;
}

/**
 * Generates an SVG string for a Code 128 barcode.
 */
export function generateBarcodeSvg(text: string, options: BarcodeSvgOptions = {}): string {
  const {
    width = 240,
    height = 80,
    showText = true,
    barColor = "#000000",
    bgColor = "transparent",
    minModuleWidth = ISO_MIN_MODULE_WIDTH_PX,
    autoExpandWidth = false,
  } = options;

  if (!text || text.trim() === "") {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
    <rect width="${width}" height="${height}" fill="#f9fafb" stroke="#e5e7eb" stroke-dasharray="4" />
    <text x="${(width / 2).toFixed(2)}" y="${(height / 2).toFixed(2)}" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="11" fill="#9ca3af">[بارکد خالی]</text>
  </svg>`;
  }

  let symbols: number[];
  try {
    symbols = encodeCode128B(text);
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : "خطای بارکد";
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
    <rect width="${width}" height="${height}" fill="#fef2f2" stroke="#f87171" />
    <text x="${(width / 2).toFixed(2)}" y="${(height / 2).toFixed(2)}" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="10" fill="#dc2626">${escapeXml(errorMsg)}</text>
  </svg>`;
  }

  // Calculate total modules
  let totalModules = 0;
  const patternList: string[] = [];

  for (const s of symbols) {
    const pattern = CODE128_PATTERNS[s] || "212222";
    patternList.push(pattern);
    for (let j = 0; j < pattern.length; j++) {
      totalModules += parseInt(pattern[j] || "0", 10);
    }
  }

  const quietZoneModules = 10;
  const grandTotalModules = totalModules + quietZoneModules * 2;
  const rawModuleWidth = width / grandTotalModules;
  const isDensityWarning = rawModuleWidth < minModuleWidth;
  const effectiveWidth = autoExpandWidth && isDensityWarning
    ? grandTotalModules * minModuleWidth
    : width;
  const moduleWidth = effectiveWidth / grandTotalModules;
  const barcodeHeight = showText ? Math.max(height - 18, 20) : height;

  let currentX = quietZoneModules * moduleWidth;
  const rects: string[] = [];

  for (const pattern of patternList) {
    for (let j = 0; j < pattern.length; j++) {
      const barModules = parseInt(pattern[j] || "0", 10);
      const barW = barModules * moduleWidth;
      const isBar = j % 2 === 0;

      if (isBar) {
        rects.push(
          `<rect x="${currentX.toFixed(2)}" y="0" width="${barW.toFixed(2)}" height="${barcodeHeight}" fill="${barColor}" />`
        );
      }
      currentX += barW;
    }
  }

  const textSvg = showText
    ? `<text x="${(effectiveWidth / 2).toFixed(2)}" y="${(height - 2).toFixed(2)}" text-anchor="middle" font-family="monospace, sans-serif" font-size="11" fill="${barColor}">${escapeXml(text)}</text>`
    : "";

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${effectiveWidth} ${height}" width="${effectiveWidth}" height="${height}" shape-rendering="crispEdges" data-module-width="${moduleWidth.toFixed(2)}"${isDensityWarning ? ' data-density-warning="true"' : ""}>
    ${bgColor !== "transparent" ? `<rect width="${effectiveWidth}" height="${height}" fill="${bgColor}" />` : ""}
    ${rects.join("")}
    ${textSvg}
  </svg>`;
}

function escapeXml(unsafe: string): string {
  return (unsafe || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
